import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Group,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Matrix4Tuple,
  Points,
  Quaternion,
  ShaderMaterial,
  Vector3,
} from 'three';

import roundPointsVColorFragmentShader from '../../../../../static/glsl/points/round-points-vcolor.frag';
import vColorVertShader from '../../../../../static/glsl/points/vcolor.vert';
import { isValidNumber } from '../../../../utils/math/number';
import { sfmViewerColors } from '../../../../viewer/elements/design/colors';
import { Wgs84Coordinates } from '../../../../viewer/utilities/math-helpers';
import { LocalGeodataImageStatus } from '../geodata-image-list/types';
import { GeodataImageItem } from '../geodata-images-state';
import { LegendsMode } from './aligner-state';

const FAILED: LocalGeodataImageStatus[] = [
  'FAILED',
  'FAILED_MISSING_CAPTURE_DATE',
  'FAILED_MISSING_GPS_DATA',
  'UPLOAD_FAILED',
];

function lerpBetweenThresholds(
  stops: { threshold: number; color: number }[],
  value: number,
): [number, number, number] {
  if (stops.length === 0) {
    return [0, 0, 0];
  }
  if (value <= stops[0].threshold) {
    return new Color(stops[0].color).toArray() as [number, number, number];
  }
  const last = stops[stops.length - 1];
  if (value >= last.threshold) {
    return new Color(last.color).toArray() as [number, number, number];
  }
  for (let i = 0; i < stops.length - 1; i++) {
    const low = stops[i];
    const high = stops[i + 1];
    if (value <= high.threshold) {
      const t = (value - low.threshold) / (high.threshold - low.threshold);
      const color = new Color(low.color).lerp(new Color(high.color), t);
      return color.toArray() as [number, number, number];
    }
  }
  return new Color(last.color).toArray() as [number, number, number];
}

// Excellent (< 0.025m): 1745
// Good (0.025-0.05m): 60
// Poor (> 0.05m): 21
const ACCURACY_COLOR_STOPS: { threshold: number; color: number }[] = [
  {
    threshold: Number.MIN_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex(),
  },
  { threshold: 0.025, color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex() },
  { threshold: 0.05, color: new Color().fromArray(sfmViewerColors.legends.critical).getHex() },
  {
    threshold: Number.MAX_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.critical).getHex(),
  },
];

function getColorsByAccuracy(standardDeviation: number): [number, number, number] {
  // No RTK data
  if (standardDeviation > 10) {
    return new Color().fromArray(sfmViewerColors.legends.none).toArray() as [
      number,
      number,
      number,
    ];
  }

  return lerpBetweenThresholds(ACCURACY_COLOR_STOPS, standardDeviation);
}

const ISO_COLOR_STOPS: { threshold: number; color: number }[] = [
  {
    threshold: Number.MIN_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex(),
  },
  { threshold: 200, color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex() },
  { threshold: 400, color: new Color().fromArray(sfmViewerColors.legends.ok).getHex() },
  { threshold: 600, color: new Color().fromArray(sfmViewerColors.legends.caution).getHex() },
  {
    threshold: Number.MAX_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.critical).getHex(),
  },
];

function getColorsByIso(iso: string | undefined): [number, number, number] {
  if (!iso || !isValidNumber(iso)) {
    return new Color().fromArray(sfmViewerColors.legends.none).toArray() as [
      number,
      number,
      number,
    ];
  }
  return lerpBetweenThresholds(ISO_COLOR_STOPS, Number(iso));
}

const SHUTTER_SPEED_COLOR_STOPS: { threshold: number; color: number }[] = [
  {
    threshold: Number.MIN_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.critical).getHex(),
  },
  { threshold: 500, color: new Color().fromArray(sfmViewerColors.legends.critical).getHex() },
  { threshold: 1000, color: new Color().fromArray(sfmViewerColors.legends.ok).getHex() },
  { threshold: 2000, color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex() },
  {
    threshold: Number.MAX_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex(),
  },
];

function getColorsByShutterSpeed(shutterSpeed: string | undefined): [number, number, number] {
  // Example: 1/2000
  const shutterSpeedNumber = shutterSpeed?.split('/')[1];
  if (!shutterSpeed || !isValidNumber(shutterSpeedNumber)) {
    return new Color().fromArray(sfmViewerColors.legends.none).toArray() as [
      number,
      number,
      number,
    ];
  }

  return lerpBetweenThresholds(SHUTTER_SPEED_COLOR_STOPS, Number(shutterSpeedNumber));
}

const APERTURE_COLOR_STOPS: { threshold: number; color: number }[] = [
  {
    threshold: Number.MIN_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.critical).getHex(),
  },
  { threshold: 0, color: new Color().fromArray(sfmViewerColors.legends.critical).getHex() },
  { threshold: 2, color: new Color().fromArray(sfmViewerColors.legends.caution).getHex() },
  { threshold: 2.8, color: new Color().fromArray(sfmViewerColors.legends.ok).getHex() },
  { threshold: 5.6, color: new Color().fromArray(sfmViewerColors.legends.excellent).getHex() },
  { threshold: 7.1, color: new Color().fromArray(sfmViewerColors.legends.ok).getHex() },
  { threshold: 8, color: new Color().fromArray(sfmViewerColors.legends.caution).getHex() },
  { threshold: 11, color: new Color().fromArray(sfmViewerColors.legends.critical).getHex() },
  {
    threshold: Number.MAX_SAFE_INTEGER,
    color: new Color().fromArray(sfmViewerColors.legends.critical).getHex(),
  },
];

function getColorsByAperture(aperture: string | undefined): [number, number, number] {
  if (!aperture || !isValidNumber(aperture)) {
    return new Color().fromArray(sfmViewerColors.legends.none).toArray() as [
      number,
      number,
      number,
    ];
  }
  return lerpBetweenThresholds(APERTURE_COLOR_STOPS, Number(aperture));
}
/**
 * Gets the RGB color for a camera point based on its status.
 */
export function getCameraPointColorRGB(
  item: GeodataImageItem,
  colors: typeof sfmViewerColors.cameras,
  legendsMode?: LegendsMode,
): [number, number, number] {
  if (legendsMode === 'accuracy') {
    return getColorsByAccuracy(item.totalStandardDeviation);
  }
  if (legendsMode === 'iso') {
    return getColorsByIso(item.exifData?.iso);
  }
  if (legendsMode === 'shutterSpeed') {
    return getColorsByShutterSpeed(item.exifData?.exposureTime);
  }
  if (legendsMode === 'aperture') {
    return getColorsByAperture(item.exifData?.apertureValue);
  }

  if (!item.enabled || item.status === 'UPLOADING') {
    return colors.disabled;
  }
  if (!item.aligned || FAILED.includes(item.status)) {
    return colors.unaligned;
  }
  if (item.status === 'PENDING') {
    return colors.pending;
  }
  return item.file ? colors.loaded : colors.default;
}

export const getPositionFromGeodataImageTransform = (transform: Matrix4Tuple): Wgs84Coordinates => {
  const position = new Vector3(0, 0, 0);
  const quaternion = new Quaternion();
  const scale = new Vector3(1, 1, 1);
  const matrix = new Matrix4().fromArray(transform);
  matrix.decompose(position, quaternion, scale);
  return {
    longitude: position.x,
    latitude: position.y,
    height: position.z,
  };
};

/**
 * Extracts the camera forward direction from a transformation matrix.
 * The forward direction in camera space is (0, 0, -1), which is transformed to world space.
 */
function getCameraDirectionFromTransform(transform: Matrix4Tuple): Vector3 {
  const quaternion = new Quaternion();
  const position = new Vector3(0, 0, 0);
  const scale = new Vector3(1, 1, 1);
  const matrix = new Matrix4().fromArray(transform);
  matrix.decompose(position, quaternion, scale);

  // Forward direction in camera space is (0, 0, -1)
  const forward = new Vector3(0, 0, -1);
  forward.applyQuaternion(quaternion);
  return forward.normalize();
}

/**
 * Creates the shader material for camera points.
 */
function createCameraPointsMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    vertexShader: vColorVertShader,
    fragmentShader: roundPointsVColorFragmentShader,
    transparent: true,
    vertexColors: true,
    uniforms: {
      size: { value: 8 },
      scale: { value: 1 },
    },
  });
}

/**
 * Creates the material for camera arrows.
 */
function createCameraArrowsMaterial(): LineBasicMaterial {
  return new LineBasicMaterial({
    color: new Color(...sfmViewerColors.cameras.default),
    transparent: true,
    opacity: 0.7,
  });
}

/**
 * Creates the material for camera arrowheads.
 */
function createCameraArrowheadsMaterial(): LineBasicMaterial {
  return new LineBasicMaterial({
    color: new Color(...sfmViewerColors.cameras.default),
    transparent: true,
    opacity: 0.7,
  });
}

/**
 * Type representing both camera geometries (points and arrows).
 */
export type CameraGeometry = {
  group: Group;
  cameras: Points;
  arrows: LineSegments;
  arrowheads: LineSegments;
  camerasGeometry: BufferGeometry;
  arrowsGeometry: BufferGeometry;
  arrowheadsGeometry: BufferGeometry;
  camerasPositions: Float32Array;
  camerasColors: Uint8Array;
  arrowsPositions: Float32Array;
  arrowheadsPositions: Float32Array;
  camerasCapacity: number;
  arrowsCapacity: number;
  arrowheadsCapacity: number;
};

/**
 * Creates and initializes the points geometry for camera positions.
 */
function createCameraPointsGeometry(initialCapacity: number): {
  points: Points;
  geometry: BufferGeometry;
  positions: Float32Array;
  colors: Uint8Array;
  capacity: number;
} {
  const geometry = new BufferGeometry();
  const positions = new Float32Array(initialCapacity * 3);
  const colors = new Uint8Array(initialCapacity * 3);

  const posAttr = new BufferAttribute(positions, 3);
  const colAttr = new BufferAttribute(colors, 3, true);

  posAttr.setUsage(DynamicDrawUsage);
  colAttr.setUsage(DynamicDrawUsage);

  geometry.setAttribute('position', posAttr);
  geometry.setAttribute('color', colAttr);

  const material = createCameraPointsMaterial();
  const points = new Points(geometry, material);

  return {
    points,
    geometry,
    positions,
    colors,
    capacity: initialCapacity,
  };
}

/**
 * Creates and initializes the arrows geometry for camera directions.
 */
function createCameraArrowsGeometry(initialCapacity: number): {
  arrows: LineSegments;
  geometry: BufferGeometry;
  positions: Float32Array;
  capacity: number;
} {
  // Each arrow is a line segment (2 vertices), so we need capacity * 2 vertices
  const geometry = new BufferGeometry();
  const positions = new Float32Array(initialCapacity * 2 * 3);

  const posAttr = new BufferAttribute(positions, 3);
  posAttr.setUsage(DynamicDrawUsage);

  geometry.setAttribute('position', posAttr);

  const material = createCameraArrowsMaterial();
  const arrows = new LineSegments(geometry, material);

  return {
    arrows,
    geometry,
    positions,
    capacity: initialCapacity,
  };
}

/**
 * Creates and initializes the arrowheads geometry for camera directions.
 * Each arrowhead consists of 2 lines extending from the tip at angles to form an arrow shape.
 * Each line has 2 vertices, so we need capacity * 2 * 2 vertices.
 */
function createCameraArrowheadsGeometry(initialCapacity: number): {
  arrowheads: LineSegments;
  geometry: BufferGeometry;
  positions: Float32Array;
  capacity: number;
} {
  // Each arrowhead has 2 lines (4 vertices total: 2 lines × 2 vertices each)
  const geometry = new BufferGeometry();
  const positions = new Float32Array(initialCapacity * 2 * 2 * 3);

  const posAttr = new BufferAttribute(positions, 3);
  posAttr.setUsage(DynamicDrawUsage);

  geometry.setAttribute('position', posAttr);

  const material = createCameraArrowheadsMaterial();
  const arrowheads = new LineSegments(geometry, material);

  // Initialize all arrowheads as hidden (far away)
  positions.fill(1e9);

  return {
    arrowheads,
    geometry,
    positions,
    capacity: initialCapacity,
  };
}

/**
 * Creates both camera points and arrows geometries with synchronized capacity.
 * This ensures both geometries have the same capacity, preventing buffer overruns
 * when arrow indices match point indices.
 */
export function createCameraGeometry(initialCapacity: number): CameraGeometry {
  const pointsData = createCameraPointsGeometry(initialCapacity);
  const arrowsData = createCameraArrowsGeometry(initialCapacity);
  const arrowheadsData = createCameraArrowheadsGeometry(initialCapacity);

  // Create a group to contain all camera-related objects
  const group = new Group();
  group.add(pointsData.points);
  group.add(arrowsData.arrows);
  group.add(arrowheadsData.arrowheads);

  return {
    group,
    cameras: pointsData.points,
    arrows: arrowsData.arrows,
    arrowheads: arrowheadsData.arrowheads,
    camerasGeometry: pointsData.geometry,
    arrowsGeometry: arrowsData.geometry,
    arrowheadsGeometry: arrowheadsData.geometry,
    camerasPositions: pointsData.positions,
    camerasColors: pointsData.colors,
    arrowsPositions: arrowsData.positions,
    arrowheadsPositions: arrowheadsData.positions,
    camerasCapacity: pointsData.capacity,
    arrowsCapacity: arrowsData.capacity,
    arrowheadsCapacity: arrowheadsData.capacity,
  };
}

/**
 * Grows the capacity of the points geometry if needed.
 */
function ensurePointsCapacity(
  geometry: BufferGeometry,
  currentPositions: Float32Array,
  currentColors: Uint8Array,
  currentCapacity: number,
  neededCapacity: number,
): {
  positions: Float32Array;
  colors: Uint8Array;
  capacity: number;
} {
  if (neededCapacity <= currentCapacity) {
    return {
      positions: currentPositions,
      colors: currentColors,
      capacity: currentCapacity,
    };
  }

  // Grow 1.5x
  const newCapacity = Math.max(neededCapacity, Math.ceil(currentCapacity * 1.5));
  const newPositions = new Float32Array(newCapacity * 3);
  const newColors = new Uint8Array(newCapacity * 3);

  newPositions.set(currentPositions);
  newColors.set(currentColors);

  geometry.setAttribute(
    'position',
    new BufferAttribute(newPositions, 3).setUsage(DynamicDrawUsage),
  );
  geometry.setAttribute(
    'color',
    new BufferAttribute(newColors, 3, true).setUsage(DynamicDrawUsage),
  );

  return {
    positions: newPositions,
    colors: newColors,
    capacity: newCapacity,
  };
}

/**
 * Grows the capacity of the arrows geometry if needed.
 */
function ensureArrowsCapacity(
  geometry: BufferGeometry,
  currentPositions: Float32Array,
  currentCapacity: number,
  neededCapacity: number,
): {
  positions: Float32Array;
  capacity: number;
} {
  if (neededCapacity <= currentCapacity) {
    return {
      positions: currentPositions,
      capacity: currentCapacity,
    };
  }

  // Grow 1.5x
  const newCapacity = Math.max(neededCapacity, Math.ceil(currentCapacity * 1.5));
  const newPositions = new Float32Array(newCapacity * 2 * 3);

  newPositions.set(currentPositions);

  geometry.setAttribute(
    'position',
    new BufferAttribute(newPositions, 3).setUsage(DynamicDrawUsage),
  );

  return {
    positions: newPositions,
    capacity: newCapacity,
  };
}

/**
 * Grows the capacity of the arrowheads geometry if needed.
 */
function ensureArrowheadsCapacity(
  geometry: BufferGeometry,
  currentPositions: Float32Array,
  currentCapacity: number,
  neededCapacity: number,
): {
  positions: Float32Array;
  capacity: number;
} {
  if (neededCapacity <= currentCapacity) {
    return {
      positions: currentPositions,
      capacity: currentCapacity,
    };
  }

  // Grow 1.5x
  const newCapacity = Math.max(neededCapacity, Math.ceil(currentCapacity * 1.5));
  // Each arrowhead has 2 lines (4 vertices: 2 lines × 2 vertices each)
  const newPositions = new Float32Array(newCapacity * 2 * 2 * 3);

  newPositions.set(currentPositions);

  // Initialize new positions as hidden
  const oldLength = currentPositions.length;
  newPositions.fill(1e9, oldLength);

  geometry.setAttribute(
    'position',
    new BufferAttribute(newPositions, 3).setUsage(DynamicDrawUsage),
  );

  return {
    positions: newPositions,
    capacity: newCapacity,
  };
}

/**
 * Ensures capacity for both camera geometries simultaneously, keeping them in sync.
 * This prevents buffer overruns when arrow indices match point indices.
 */
export function ensureCameraGeometryCapacity(
  geometry: CameraGeometry,
  neededCapacity: number,
): CameraGeometry {
  const pointsResult = ensurePointsCapacity(
    geometry.camerasGeometry,
    geometry.camerasPositions,
    geometry.camerasColors,
    geometry.camerasCapacity,
    neededCapacity,
  );

  const arrowsResult = ensureArrowsCapacity(
    geometry.arrowsGeometry,
    geometry.arrowsPositions,
    geometry.arrowsCapacity,
    neededCapacity,
  );

  const arrowheadsResult = ensureArrowheadsCapacity(
    geometry.arrowheadsGeometry,
    geometry.arrowheadsPositions,
    geometry.arrowheadsCapacity,
    neededCapacity,
  );

  return {
    ...geometry,
    camerasPositions: pointsResult.positions,
    camerasColors: pointsResult.colors,
    camerasCapacity: pointsResult.capacity,
    arrowsPositions: arrowsResult.positions,
    arrowsCapacity: arrowsResult.capacity,
    arrowheadsPositions: arrowheadsResult.positions,
    arrowheadsCapacity: arrowheadsResult.capacity,
  };
}

/**
 * Updates a camera point's position in the buffer.
 */
function updateCameraPointPosition(
  positions: Float32Array,
  index: number,
  x: number,
  y: number,
  z: number,
): boolean {
  const baseIdx = index * 3;
  if (positions[baseIdx] !== x || positions[baseIdx + 1] !== y || positions[baseIdx + 2] !== z) {
    positions[baseIdx] = x;
    positions[baseIdx + 1] = y;
    positions[baseIdx + 2] = z;
    return true;
  }
  return false;
}

/**
 * Updates a camera point's color in the buffer.
 */
function updateCameraPointColor(
  colors: Uint8Array,
  index: number,
  r: number,
  g: number,
  b: number,
): boolean {
  const baseIdx = index * 3;
  const R = (r * 255) | 0;
  const G = (g * 255) | 0;
  const B = (b * 255) | 0;

  if (colors[baseIdx] !== R || colors[baseIdx + 1] !== G || colors[baseIdx + 2] !== B) {
    colors[baseIdx] = R;
    colors[baseIdx + 1] = G;
    colors[baseIdx + 2] = B;
    return true;
  }
  return false;
}

/**
 * Updates a camera arrow's position in the buffer.
 */
function updateCameraArrow(
  positions: Float32Array,
  index: number,
  startX: number,
  startY: number,
  startZ: number,
  endX: number,
  endY: number,
  endZ: number,
): boolean {
  const baseIdx = index * 2 * 3; // 2 vertices per arrow, 3 components per vertex

  if (
    positions[baseIdx + 0] !== startX ||
    positions[baseIdx + 1] !== startY ||
    positions[baseIdx + 2] !== startZ ||
    positions[baseIdx + 3] !== endX ||
    positions[baseIdx + 4] !== endY ||
    positions[baseIdx + 5] !== endZ
  ) {
    positions[baseIdx + 0] = startX;
    positions[baseIdx + 1] = startY;
    positions[baseIdx + 2] = startZ;
    positions[baseIdx + 3] = endX;
    positions[baseIdx + 4] = endY;
    positions[baseIdx + 5] = endZ;
    return true;
  }
  return false;
}

/**
 * Hides a camera point by moving it far away.
 */
function hideCameraPoint(positions: Float32Array, index: number): void {
  const baseIdx = index * 3;
  positions[baseIdx] = positions[baseIdx + 1] = positions[baseIdx + 2] = 1e9;
}

/**
 * Hides a camera arrow by moving it far away.
 */
function hideCameraArrow(positions: Float32Array, index: number): void {
  const baseIdx = index * 2 * 3;
  positions[baseIdx + 0] = positions[baseIdx + 3] = 1e9;
  positions[baseIdx + 1] = positions[baseIdx + 4] = 1e9;
  positions[baseIdx + 2] = positions[baseIdx + 5] = 1e9;
}

/**
 * Hides a camera arrowhead by moving it far away.
 */
function hideCameraArrowhead(positions: Float32Array, index: number): void {
  // Each arrowhead has 2 lines (4 vertices: 2 lines × 2 vertices each)
  const baseIdx = index * 2 * 2 * 3;
  for (let i = 0; i < 4 * 3; i++) {
    positions[baseIdx + i] = 1e9;
  }
}

/**
 * Updates a camera arrowhead's position and orientation.
 * The arrowhead follows the arrow (same plane as the shaft); it is rotated around
 * the tip so it is optimally visible when viewed from above (z axis).
 */
function updateCameraArrowhead(
  positions: Float32Array,
  index: number,
  tipPosition: Vector3,
  direction: Vector3,
): boolean {
  // Each arrowhead has 2 lines (4 vertices: 2 lines × 2 vertices each)
  const baseIdx = index * 2 * 2 * 3;

  const arrowheadLength = 0.25;
  const arrowheadAngle = Math.PI / 6;

  const backwardDir = direction.clone().multiplyScalar(-1).normalize();

  // Perpendicular in the plane that contains the shaft (for spreading the two lines)
  const perp = new Vector3().crossVectors(new Vector3(0, 0, 1), backwardDir);
  if (perp.length() < 0.001) {
    perp.crossVectors(new Vector3(1, 0, 0), backwardDir);
  }
  perp.normalize();

  let leftDir = backwardDir
    .clone()
    .multiplyScalar(Math.cos(arrowheadAngle))
    .add(perp.clone().multiplyScalar(Math.sin(arrowheadAngle)))
    .normalize();
  let rightDir = backwardDir
    .clone()
    .multiplyScalar(Math.cos(arrowheadAngle))
    .add(perp.clone().multiplyScalar(-Math.sin(arrowheadAngle)))
    .normalize();

  // Rotate arrowhead around the shaft so the opening faces upward (best visibility from z).
  // Use axis-angle rotation via Quaternion to preserve the cone angle (full Rodrigues);
  // a truncated rotation would distort leftDir/rightDir magnitudes before normalization.
  const shaft = direction.clone().normalize();
  const planeNormal = new Vector3().crossVectors(leftDir, rightDir);
  const crossShaftNormal = new Vector3().crossVectors(shaft, planeNormal);
  const phi = Math.atan2(crossShaftNormal.z, planeNormal.z);
  const quat = new Quaternion().setFromAxisAngle(shaft, phi);
  leftDir = leftDir.applyQuaternion(quat);
  rightDir = rightDir.applyQuaternion(quat);

  // Check if we need to update (compare first vertex position)
  const firstVertexIdx = baseIdx;
  const needsUpdate =
    Math.abs(positions[firstVertexIdx] - tipPosition.x) > 0.001 ||
    Math.abs(positions[firstVertexIdx + 1] - tipPosition.y) > 0.001 ||
    Math.abs(positions[firstVertexIdx + 2] - tipPosition.z) > 0.001;

  if (!needsUpdate) {
    return false;
  }

  // Set vertices for both lines
  const arrowheadDirs = [leftDir, rightDir];
  for (let lineIdx = 0; lineIdx < 2; lineIdx++) {
    const lineBaseIdx = baseIdx + lineIdx * 2 * 3;
    const arrowheadDir = arrowheadDirs[lineIdx];
    const endPosition = tipPosition
      .clone()
      .add(arrowheadDir.clone().multiplyScalar(arrowheadLength));

    // Start vertex (at tip)
    positions[lineBaseIdx + 0] = tipPosition.x;
    positions[lineBaseIdx + 1] = tipPosition.y;
    positions[lineBaseIdx + 2] = tipPosition.z;

    // End vertex (angled away from tip)
    positions[lineBaseIdx + 3] = endPosition.x;
    positions[lineBaseIdx + 4] = endPosition.y;
    positions[lineBaseIdx + 5] = endPosition.z;
  }

  return true;
}

/**
 * Hides both camera point and arrow at the same index simultaneously.
 * This ensures both geometries stay in sync when hiding elements.
 */
export function hideCameraGeometryIndex(geometry: CameraGeometry, index: number): void {
  hideCameraPoint(geometry.camerasPositions, index);
  hideCameraArrow(geometry.arrowsPositions, index);
  hideCameraArrowhead(geometry.arrowheadsPositions, index);
}

/**
 * Updates camera points and arrows for a collection of images.
 * This is a helper function that handles the common iteration pattern and optionally finalizes geometry updates.
 */
export function updateCameraGeometry<T>(options: {
  images: T[];
  geometry: CameraGeometry;
  getPointIndex: (image: T, arrayIndex: number) => number;
  getPosition: (image: T) => { x: number; y: number; z: number };
  getColor: (image: T) => [number, number, number];
  getArrowIndex?: (image: T, pointIndex: number, arrayIndex: number) => number;
  getTransform?: (image: T) => Matrix4Tuple | undefined;
  shouldProcessImage?: (image: T) => boolean;
  hasStructuralChange?: boolean;
}) {
  const {
    images,
    geometry,
    getPointIndex,
    getPosition,
    getColor,
    getArrowIndex,
    getTransform,
    shouldProcessImage,
    hasStructuralChange = false,
  } = options;

  const positions = geometry.camerasPositions;
  const colors = geometry.camerasColors;
  const arrowsPositions = geometry.arrowsPositions;
  const arrowheadsPositions = geometry.arrowheadsPositions;

  let posDirty = false;
  let colDirty = false;
  let arrowsDirty = false;
  let arrowheadsDirty = false;
  let arrowIndex = 0;

  for (let i = 0; i < images.length; i++) {
    const image = images[i];

    if (shouldProcessImage && !shouldProcessImage(image)) {
      continue;
    }

    const pointIdx = getPointIndex(image, i);
    const position = getPosition(image);
    const [r, g, b] = getColor(image);

    // Update point position
    if (updateCameraPointPosition(positions, pointIdx, position.x, position.y, position.z)) {
      posDirty = true;
    }

    // Update point color
    if (updateCameraPointColor(colors, pointIdx, r, g, b)) {
      colDirty = true;
    }

    // Update arrow if transform is available
    if (getTransform) {
      const transform = getTransform(image);
      if (transform) {
        const direction = getCameraDirectionFromTransform(transform);
        const arrowStart = new Vector3(position.x, position.y, position.z);
        const arrowLength = 1.5;
        const arrowEnd = arrowStart.clone().add(direction.clone().multiplyScalar(arrowLength));

        const arrowIdx = getArrowIndex ? getArrowIndex(image, pointIdx, i) : arrowIndex;
        if (
          updateCameraArrow(
            arrowsPositions,
            arrowIdx,
            arrowStart.x,
            arrowStart.y,
            arrowStart.z,
            arrowEnd.x,
            arrowEnd.y,
            arrowEnd.z,
          )
        ) {
          arrowsDirty = true;
        }

        // Update arrowhead position and orientation
        if (updateCameraArrowhead(arrowheadsPositions, arrowIdx, arrowEnd, direction)) {
          arrowheadsDirty = true;
        }

        if (!getArrowIndex) {
          arrowIndex++;
        }
      }
    }
  }

  // Finalize geometry updates
  finalizePointsGeometryUpdate(geometry.camerasGeometry, posDirty, colDirty, hasStructuralChange);

  if (arrowsDirty) {
    finalizeArrowsGeometryUpdate(geometry.arrowsGeometry, arrowsDirty);
  }

  if (arrowheadsDirty) {
    finalizeArrowsGeometryUpdate(geometry.arrowheadsGeometry, arrowheadsDirty);
  }
}

/**
 * Marks geometry attributes as needing update and computes bounding sphere if needed.
 */
function finalizePointsGeometryUpdate(
  geometry: BufferGeometry,
  positionDirty: boolean,
  colorDirty: boolean,
  hasStructuralChange: boolean,
): void {
  if (positionDirty) {
    (geometry.attributes.position as BufferAttribute).needsUpdate = true;

    if (hasStructuralChange || !geometry.boundingSphere) {
      geometry.computeBoundingSphere();
    }
  }

  if (colorDirty) {
    (geometry.attributes.color as BufferAttribute).needsUpdate = true;
  }
}

/**
 * Marks arrows geometry attributes as needing update and computes bounding sphere.
 */
function finalizeArrowsGeometryUpdate(geometry: BufferGeometry, dirty: boolean): void {
  if (dirty) {
    (geometry.attributes.position as BufferAttribute).needsUpdate = true;
    geometry.computeBoundingSphere();
  }
}
